import { equal } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { AdminTaskStatus } from '@/client/admin-tasks/types';
import { Actions, Subjects } from '@/client/users';
import {
  DashboardType,
  LAST_SELECTED_DASHBOARD,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { FreeTrialBox } from '@/components/navigation/FreeTrialBox';
import { SidebarAccountName } from '@/components/navigation/SidebarAccountName';
import { SidebarAccountSelect } from '@/components/navigation/SidebarAccountSelect';
import { useAccountAdminTasks } from '@/hooks/query/account-admin-tasks.hooks';
import { useAppSelector } from '@/hooks/store';
import { useAwarenessHidden } from '@/hooks/useAwarenessHidden';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { isDemoModeActive } from '@/utils/demo-mode';
import {
  accountHasDocuments,
  AdminTasksIconPath,
  AwarenessReportsIconPath,
  branchAdminCheck,
  CampaignsIconPath,
  CoursesIconPath,
  EmailTemplatesIconPath,
  FindUserIconPath,
  getShouldShowPhishingIntro,
  HomeIconPath,
  HubspotErrorLogIconPath,
  PageTemplatesIconPath,
  PhishingGlobalSettingsIcon,
  PhishingIconPath,
  RedirectTemplatesIconPath,
  ResellersIconPath,
  ScheduleIconPath,
  SenderIconPath,
  SimulationTemplatesIconPath,
  SummaryReportsIconPath,
  SurveyIconPath,
  SurveySendIconPath,
  SyncIconPath,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import classNames from 'classnames';
import { ChevronDownIcon } from 'primereact/icons/chevrondown';
import { ChevronRightIcon } from 'primereact/icons/chevronright';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { PanelMenu as PrimePanelMenu } from 'primereact/panelmenu';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledPanelMenu = styled(PrimePanelMenu)`
  min-width: min-content;
  &.p-panelmenu {
    width: 100%;
    border: none;

    .p-panelmenu-panel {
      margin: 0 var(--medium-padding);

      .p-toggleable-content {
        border-bottom: 1px solid var(--purple-highlight);
        margin: 0 var(--default-padding);
      }

      .p-panelmenu-header {
        .p-panelmenu-header-link {
          margin: 0 var(--default-padding);
          border-bottom: 1px solid var(--purple-highlight);
        }

        &:not(.p-highlight) .p-panelmenu-header-content {
          :not(.p-disabled) > a:not(.p-highlight):hover {
            background: var(--purple-dark);
            color: var(--purple-light);
            border-bottom: 1px solid var(--purple-highlight);
          }
        }

        &.p-highlight {
          .p-panelmenu-header-link {
            border-bottom: 1px solid transparent;
          }
          > a {
            border-radius: 0;
            border: none;
            background: var(--purple-dark);
            color: var(--white-main);
            border-color: var(--purple-highlight);
            box-shadow: none;
          }

          &:not(.p-disabled):not(.link-header) > a:hover {
            background: var(--purple-dark);
            color: var(--white-main);
            border-radius: 0;
            border-bottom: 1px solid transparent;
          }
        }
      }

      .p-panelmenu-header-link {
        padding: var(--medium-padding) 0;
        font-size: var(--xsmall-font-size);
        line-height: var(--xsmall-line-height);
        text-transform: uppercase;
        font-weight: 500;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: var(--white-main);

        .p-submenu-icon {
          margin-left: auto;
        }

        svg {
          margin-right: var(--small-padding);
        }

        &:focus,
        &.p-highlight {
          box-shadow: none;
        }

        &.link-header {
          border: 1px solid transparent;
          border-bottom: 1px solid var(--purple-highlight);
          padding-bottom: calc(var(--medium-padding) + 1px);
          &.p-highlight {
            top: -1px;
            overflow: hidden;
            border: 1px solid var(--purple-highlight);
            border-radius: var(--default-border-radius);
            padding: var(--medium-padding) var(--default-padding);
            padding-top: calc(var(--medium-padding) + 1px);
            margin: 0;
            &:not(.p-disabled):hover {
              color: var(--white-main);
              border: 1px solid var(--purple-highlight);
            }
          }
        }
      }

      .p-panelmenu-content {
        font-size: var(--small-font-size);
        line-height: var(--small-line-height);
        padding: 0;
        margin-bottom: var(--xsmall-padding);
        border: 0;
        border-radius: 0;
        background: var(--purple-dark);
        color: var(--white-main);

        .p-menuitem {
          margin-bottom: var(--xsmall-padding);
          .p-menuitem-icon {
            font-size: var(--small-font-size);
            margin-right: var(--small-padding);
            color: var(--white-main);
          }

          .p-menuitem-text {
            font-size: var(--small-font-size);
            line-height: var(--small-line-height);
          }

          .p-menuitem-link {
            background: var(--purple-dark);
            padding: var(--small-padding) var(--medium-padding);
            border: 1px solid transparent;

            .custom-icon {
              flex-basis: 24px;
              min-width: 24px;
            }

            &:focus {
              box-shadow: var(--box-shadow-red-inset);
              border: 1px solid var(--purple-highlight);
              border-radius: var(--default-border-radius);
            }
            &.p-menuitem-link-active {
              border: 1px solid var(--purple-highlight);
              border-radius: var(--default-border-radius);
              overflow: hidden;
            }

            .p-menuitem-text {
              color: var(--white-main);
            }

            &:not(.p-disabled):hover {
              background: var(--purple-dark);
              color: var(--purple-light);

              .custom-icon {
                fill: var(--purple-light);
              }

              .p-menuitem-icon,
              .p-menuitem-text {
                color: var(--purple-light);
              }

              &.p-menuitem-link-active {
                .p-menuitem-icon,
                .p-menuitem-text {
                  color: var(--white-main);
                }
                .custom-icon {
                  fill: var(--white-main);
                }
              }
            }
            .custom-icon {
              fill: var(--white-main);
            }
          }
        }
      }

      .p-submenu-icon {
        order: 1;
        font-size: var(--medium-font-size);
        margin-right: 0;
        margin-left: var(--small-padding);
      }

      &.section-title {
        opacity: 100%;
        .p-panelmenu-header-link {
          text-transform: capitalize;
          margin-top: var(--small-padding);
          padding-top: var(--default-padding);
          padding-bottom: var(--small-padding);
          color: var(--purple-light);
          border-bottom: none;
        }
      }
    }
    .p-panelmenu-panel:last-child {
      .p-panelmenu-header {
        .p-panelmenu-header-link:not(.p-highlight) {
          border-bottom: 1px solid transparent;
        }
        &:not(.p-highlight) .p-panelmenu-header-content {
          :not(.p-disabled) > a:hover {
            border-color: transparent;
            &.p-highlight {
              &.link-header {
                border-color: var(--purple-highlight);
              }
            }
          }
        }
      }
      .p-toggleable-content:last-child {
        border-bottom: 1px solid transparent;
      }
    }
  }
`;

const StyledSidebarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--red-dark);
  color: var(--white-main);
  border-radius: var(--grand-border-radius);
  font-size: var(--xsmall-font-size);
  line-height: 150%;
  font-weight: 500;
  min-height: 18px;
  width: 18px;
  white-space: nowrap;
  margin-left: var(--semi-xsmall-padding);
  padding: 0 var(--semi-xsmall-padding);
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-width: 240px;
`;

const StyledFooter = styled.footer`
  font-size: var(--xsmall-font-size);
  line-height: var(--small-line-height);
  color: var(--white-main);
  font-weight: 500;
  padding: var(--default-padding) var(--default-padding) 0;
  margin: 0 var(--medium-padding);

  span,
  a {
    color: var(--purple-light);
  }

  a {
    text-decoration: none;
  }
`;

const getItemTemplate =
  (url: string, current: string, badgeCount?: number) => (item: MenuItem) => {
    const getUrl = /[^/]*.[^/]*/.exec(url);
    const regex = new RegExp(`^${getUrl?.length ? getUrl[0] : url}$`);

    const currentLocationStart = `/${current.split('/')[1]}`;

    return (
      <Link
        to={url}
        className={classNames({
          'p-menuitem-link': true,
          'p-menuitem-link-active': currentLocationStart.match(regex),
        })}
        role="menuitem"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className="p-menuitem-icon custom-icon"
        >
          {item.icon}
        </svg>
        <span className="p-menuitem-text">{item.label}</span>
        {!!badgeCount && <StyledSidebarBadge>{badgeCount}</StyledSidebarBadge>}
      </Link>
    );
  };

const getHomePanelTemplate =
  (badgeCount: number) => (item: MenuItem, options: any) =>
    (
      <a
        className="p-panelmenu-header-link"
        onClick={options.onClick}
        href="#"
        tabIndex={-1}
        role="button"
      >
        <span className="p-menuitem-text">{item.label}</span>
        {!options.active && !!badgeCount && (
          <StyledSidebarBadge>{badgeCount}</StyledSidebarBadge>
        )}
        {options.active ? (
          <ChevronDownIcon className="p-submenu-icon" />
        ) : (
          <ChevronRightIcon className="p-submenu-icon" />
        )}
      </a>
    );

const getItemTemplateHeader =
  (
    url: string,
    current: string,
    matchStart?: boolean,
    excludePrefixes?: string[],
  ) =>
  (item: MenuItem, options?: MenuItemOptions & { active?: boolean }) => {
    const getUrl = /[^/]*.[^/]*/.exec(url);
    const regex = new RegExp(`^${getUrl?.length ? getUrl[0] : url}$`);

    const currentLocationStart = `/${current.split('/')[1]}`;

    const isExcluded = excludePrefixes?.some((prefix) =>
      current.startsWith(prefix),
    );

    return (
      <Link
        to={url}
        className={classNames({
          'p-panelmenu-header-link': true,
          'p-highlight':
            !isExcluded &&
            (matchStart
              ? currentLocationStart.match(regex)
              : current.includes(url)),
          'link-header': !item.items,
        })}
        role="menuitem"
      >
        <span className="p-menuitem-text">{item.label}</span>
        {!!item.items &&
          (options?.active ? (
            <ChevronDownIcon className="p-submenu-icon" />
          ) : (
            <ChevronRightIcon className="p-submenu-icon" />
          ))}
      </Link>
    );
  };

const getMenu = (
  can: (action: Actions, subject: Subjects) => boolean,
  cannot: (action: Actions, subject: Subjects) => boolean,
  accountCanUseFeature: (
    feature: Subjects | PostHogFeatureFlags,
    featureFlagAbilities: any,
  ) => boolean,
  accountsCount: number,
  onClick: (route: string) => void,
  current: string,
  t: TranslationFunctionType,
  account: Account | null,
  isBranchAdmin: boolean,
  incompleteTaskCount: number,
  showPhishingIntro: boolean,
  isAwarenessHidden: boolean,
): MenuItem[] => {
  const isSystem = account?.isSystem;
  const isCommercialAdminOnSystem =
    isSystem &&
    can(Actions.READ, Subjects.ACCOUNTS) &&
    !can(Actions.READ, Subjects.ACCOUNTS_SYSTEM_PAGE);
  const hasAccountsAdminLink =
    (can(Actions.READ, Subjects.ACCOUNTS_SYSTEM_PAGE) ||
      (can(Actions.READ, Subjects.ACCOUNTS) && !isSystem)) &&
    cannot(Actions.READ, Subjects.OWN_ACCOUNT);
  const menu: MenuItem[] = [];
  const homeSubMenu: MenuItem[] = [];
  const coursesSubMenu: MenuItem[] = [];
  const campaignSubMenu: MenuItem[] = [];
  const securityCultureSubMenu: MenuItem[] = [];
  const tools: MenuItem[] = [];
  const phishingControlsSubMenu: MenuItem[] = [];
  const hasDocuments = accountHasDocuments(account) && !isAwarenessHidden;

  if (isCommercialAdminOnSystem) {
    homeSubMenu.push({
      label: t('generic.dashboard'),
      icon: <HomeIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD](),
        current,
        incompleteTaskCount,
      ),
    });

    menu.push({
      label: t('sidebar.home'),
      items: homeSubMenu,
      expanded: true,
    });

    if (can(Actions.READ, Subjects.USERS)) {
      tools.push({
        label: t('user.find'),
        icon: <FindUserIconPath />,
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.FIND_USER](),
          current,
        ),
      });

      menu.push({
        label: t('sidebar.tools'),
        items: tools,
        expanded: !!current.match(/^\/find-user/),
      });
    }

    return menu;
  }

  // ------------------ HOME ------------------ //

  if (can(Actions.READ, Subjects.ADMIN_DASHBOARD)) {
    const isHomeExpanded = !!current.match(
      /^\/(dashboard|admin-tasks|reports-summary)/,
    );

    homeSubMenu.push({
      label: t('generic.dashboard'),
      icon: <HomeIconPath />,
      template: getItemTemplate(
        localStorage.getItem(LAST_SELECTED_DASHBOARD) === DashboardType.LEARNER
          ? RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]()
          : RedirectPaths[RedirectPathsEnum.ADMIN_DASHBOARD](),
        current,
        incompleteTaskCount,
      ),
    });

    if (isSystem) {
      homeSubMenu.push({
        label: t('sidebar.adminTasks'),
        icon: <AdminTasksIconPath />,
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.ADMIN_TASKS](),
          current,
        ),
      });
    }

    if (!isSystem) {
      homeSubMenu.push({
        label: t('sidebar.userSummary'),
        icon: <SummaryReportsIconPath />,
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
          current,
        ),
      });
    }

    if (homeSubMenu.length > 0) {
      menu.push({
        label: t('sidebar.home'),
        items: homeSubMenu,
        expanded: isHomeExpanded,
        template: getHomePanelTemplate(incompleteTaskCount),
      });
    }
  }

  // ------------------ TRAININGS ------------------ //
  menu.push({
    label: t('sidebar.trainings'),
    className: 'section-title p-disabled',
  });

  // ------------------ TRAININGS-AWARENESS ------------------ //

  if (can(Actions.READ, Subjects.ACCOUNT_REPORTS)) {
    coursesSubMenu.push({
      label: t('sidebar.analytics'),
      icon: <PhishingIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.CONTENT_ANALYTICS](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.COURSES)) {
    coursesSubMenu.push({
      label: t('content'),
      icon: <CoursesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.COURSES](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.COURSE_PLANNER) && !isSystem) {
    coursesSubMenu.push({
      label: t('courses.trainingPlanner'),
      icon: <ScheduleIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_PLANNER](),
        current,
      ),
    });
  }
  if (can(Actions.READ, Subjects.ACCOUNT_REPORTS) && !isSystem) {
    coursesSubMenu.push({
      label: t('sidebar.awarenessReports'),
      icon: <AwarenessReportsIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.REPORTS_AWARENESS](),
        current,
      ),
    });
  }

  if (coursesSubMenu.length > 0 && !isAwarenessHidden) {
    menu.push({
      label: t('sidebar.awareness'),
      items: coursesSubMenu,
      expanded: !!current.match(
        /^\/(awareness-training-analytics|content|enrollment|reports-awareness)/,
      ),
    });
  }

  // ------------------ TRAININGS-PHISHING ------------------ //

  if (can(Actions.READ, Subjects.CAMPAIGNS) && showPhishingIntro) {
    campaignSubMenu.push({
      label: t('sidebar.phishing.intro'),
      icon: <PhishingIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.PHISHING_INTRO](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.ACCOUNT_REPORTS)) {
    campaignSubMenu.push({
      label: t('sidebar.analytics'),
      icon: <PhishingIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.PHISHING_ANALYTICS](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.CAMPAIGNS) && !account?.isSystem) {
    campaignSubMenu.push({
      label: t('sidebar.phishing.autoPhishing'),
      icon: <CampaignsIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.PHISHING_SIMULATIONS](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.CAMPAIGNS)) {
    campaignSubMenu.push({
      label: t('campaigns'),
      icon: <CampaignsIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.CAMPAIGNS](),
        current,
      ),
    });
  }

  if (isSystem && can(Actions.READ, Subjects.SUPPRESSED_DOMAINS)) {
    campaignSubMenu.push({
      label: t('simulationTemplates.sidebar'),
      icon: <SimulationTemplatesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.EMAIL_TEMPLATES)) {
    phishingControlsSubMenu.push({
      label: t('templates.emailTemplates'),
      icon: <EmailTemplatesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.EMAIL_TEMPLATES_PREDEFINED](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.PAGES)) {
    phishingControlsSubMenu.push({
      label: t('templates.pageTemplates'),
      icon: <PageTemplatesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_PREDEFINED](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.PAGES)) {
    phishingControlsSubMenu.push({
      label: t('templates.redirectTemplates'),
      icon: <RedirectTemplatesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.REDIRECT_PAGE_TEMPLATES_PREDEFINED](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.SENDING_PROFILES)) {
    phishingControlsSubMenu.push({
      label: t('sender'),
      icon: <SenderIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.SENDER_PROFILES_PREDEFINED](),
        current,
      ),
    });
  }

  if (isSystem && can(Actions.READ, Subjects.SUPPRESSED_DOMAINS)) {
    campaignSubMenu.push({
      label: t('globalSettings'),
      icon: <PhishingGlobalSettingsIcon />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.PHISHING_GLOBAL_SETTINGS](),
        current,
      ),
    });
  }

  if (campaignSubMenu.length > 0) {
    menu.push({
      label: t('campaign.phishing'),
      items: campaignSubMenu,
      expanded: !!current.match(
        /^\/(campaigns|phishing-analytics|phishing-global-settings|phishing-simulations|campaign-master-templates|phishing-intro)/,
      ),
      ...(showPhishingIntro && {
        template: getItemTemplateHeader(
          RedirectPaths[RedirectPathsEnum.PHISHING_INTRO](),
          current,
        ),
      }),
    });
  }

  // ------------------ SECURITY CULTURE ------------------ //

  const useSimplifiedSCPlanner = accountCanUseFeature(
    PostHogFeatureFlags.SIMPLIFIED_SC_SURVEY_PLANNER,
    account?.featureFlagAbility ?? [],
  );

  if (can(Actions.READ, Subjects.SECURITY_CULTURE_REPORTS)) {
    securityCultureSubMenu.push({
      label: isSystem
        ? t('generic.dashboard')
        : t('security.culture.analytics'),
      icon: <SummaryReportsIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](),
        current,
      ),
    });
  }

  if (
    !isSystem &&
    useSimplifiedSCPlanner &&
    can(Actions.READ, Subjects.SECURITY_CULTURE)
  ) {
    securityCultureSubMenu.push({
      label: t('security.culture.survey.planner'),
      icon: <SurveySendIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY_PLANNER](),
        current,
      ),
    });
  }

  if (
    (!useSimplifiedSCPlanner || isSystem) &&
    can(Actions.READ, Subjects.SECURITY_CULTURE)
  ) {
    securityCultureSubMenu.push({
      label: t('security.culture.survey'),
      icon: <SurveyIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.SURVEY](),
        current,
      ),
    });
  }

  if (
    can(Actions.CREATE, Subjects.SECURITY_CULTURE) &&
    (!useSimplifiedSCPlanner || isSystem)
  ) {
    securityCultureSubMenu.push({
      label: t('security.culture.reports'),
      icon: <PageTemplatesIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE](),
        current,
      ),
    });

    if (!isSystem && !useSimplifiedSCPlanner) {
      securityCultureSubMenu.push({
        label: t('security.culture.send.survey'),
        icon: <SurveySendIconPath />,
        template: getItemTemplate(
          RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SEND_SURVEY](),
          current,
        ),
      });
    }
  }

  if (securityCultureSubMenu?.length) {
    menu.push({
      label: t('security.culture'),
      items: securityCultureSubMenu,
      expanded: !!current.match(
        /^\/(security-culture|upload-survey|send-survey|survey)/,
      ),
    });
  }

  // ------------------ MATERIALS ------------------ //
  if (hasDocuments || account?.isSystem) {
    menu.push({
      label: t('sidebar.materials'),
      className: 'section-title p-disabled',
    });
  }

  if (
    can(Actions.READ, Subjects.RESOURCES_PAGE) &&
    (hasDocuments || account?.isSystem)
  ) {
    menu.push({
      label: t('templates'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.RESOURCES](),
        current,
        false,
        [RedirectPaths[RedirectPathsEnum.POSTERS]()],
      ),
    });
    menu.push({
      label: t('posters'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.POSTERS](),
        current,
      ),
    });
  }

  // ------------------ SETTINGS ------------------ //
  if (
    can(Actions.READ, Subjects.ACCOUNTS) ||
    can(Actions.READ, Subjects.ACCOUNTS_SYSTEM_PAGE) ||
    can(Actions.READ, Subjects.ACCOUNT_SETTINGS_PAGE) ||
    can(Actions.READ, Subjects.USERS) ||
    can(Actions.READ, Subjects.BRANCHES) ||
    (!isBranchAdmin && can(Actions.READ, Subjects.GROUPS)) ||
    can(Actions.READ, Subjects.SYSTEM_SETTINGS) ||
    (can(Actions.READ, Subjects.FEATURE_FLAGS) && isSystem) ||
    isSystem
  ) {
    menu.push({
      label: t('sidebar.settings'),
      className: 'section-title p-disabled',
    });
  }

  if (hasAccountsAdminLink) {
    menu.push({
      label: isSystem ? t('accounts') : t('account'),
      template: getItemTemplateHeader(
        isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNTS]()
          : account
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.CREATE_ACCOUNT](),
        current,
        true,
      ),
    });
  }

  if (
    !hasAccountsAdminLink &&
    can(Actions.READ, Subjects.ACCOUNT_SETTINGS_PAGE) &&
    account
  ) {
    menu.push({
      label: t('account'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id),
        current,
        true,
      ),
    });
  }

  if (can(Actions.READ, Subjects.USERS)) {
    menu.push({
      label: t('users'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.USERS](),
        current,
        true,
      ),
    });
  }

  if (can(Actions.READ, Subjects.BRANCHES)) {
    menu.push({
      label: t('branches'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.BRANCHES](),
        current,
        true,
      ),
    });
  }

  if (!isBranchAdmin && can(Actions.READ, Subjects.GROUPS)) {
    menu.push({
      label: t('groups'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.GROUPS](),
        current,
        true,
      ),
    });
  }

  if (can(Actions.READ, Subjects.SYSTEM_SETTINGS)) {
    menu.push({
      label: t('sidebar.emails'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.SYSTEM_EMAILS_TEMPLATES](),
        current,
        true,
      ),
    });
  }

  if (can(Actions.READ, Subjects.FEATURE_FLAGS) && isSystem) {
    menu.push({
      label: t('sidebar.features'),
      template: getItemTemplateHeader(
        RedirectPaths[RedirectPathsEnum.FEATURES](),
        current,
        true,
      ),
    });
  }

  // ------------------ SETTINGS-TOOLS ------------------ //

  if (can(Actions.READ, Subjects.HUBSPOT_ACCOUNT_LINK) && isSystem) {
    tools.push({
      label: t('hubspot.forceSync'),
      icon: <SyncIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.HUBSPOT_SYNC](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.HUBSPOT_ERROR_LOG) && isSystem) {
    tools.push({
      label: t('sidebar.hubspotErrorLog'),
      icon: <HubspotErrorLogIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.HUBSPOT_ERROR_LOG](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.RESELLERS) && isSystem) {
    tools.push({
      label: t('sidebar.resellers'),
      icon: <ResellersIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.RESELLERS](),
        current,
      ),
    });
  }

  if (can(Actions.READ, Subjects.USERS) && isSystem) {
    tools.push({
      label: t('user.find'),
      icon: <FindUserIconPath />,
      template: getItemTemplate(
        RedirectPaths[RedirectPathsEnum.FIND_USER](),
        current,
      ),
    });
  }

  if (tools.length > 0) {
    menu.push({
      label: t('sidebar.tools'),
      items: tools,
      expanded: !!current.match(
        /^\/(hubspot-sync|hubspot-errors|resellers|find-user)/,
      ),
    });
  }

  if (phishingControlsSubMenu.length > 0) {
    menu.push({
      label: t('sidebar.phishingControls'),
      items: phishingControlsSubMenu,
      expanded: !!current.match(
        /^\/(email-templates|landing-page-templates|redirect-page-templates|sender-profiles)/,
      ),
    });
  }

  return menu;
};

export const SidebarMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectCurrentUser);
  const { can, cannot, canReach } = usePermission();
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const current = location.pathname;
  const account = useAppSelector(selectCurrentAccount);
  const showPhishingIntro =
    canUseFeature(PostHogFeatureFlags.PHISHING_INTRO_PAGE) &&
    getShouldShowPhishingIntro(account);
  const isAwarenessHidden = useAwarenessHidden(account);
  const isBranchAdmin = branchAdminCheck(user, account);

  const { accountAdminTasks: incompleteTasks } = useAccountAdminTasks(
    account?.id as string,
    {
      filters: [equal('status', AdminTaskStatus.TODO)],
      enabled:
        !account?.isSystem &&
        !isDemoModeActive() &&
        can(Actions.READ, Subjects.ADMIN_TASKS),
    },
  );
  const incompleteTaskCount = incompleteTasks?.result?.length ?? 0;

  const onClick: (url: string) => void = (url: string) => navigate(url);

  return (
    <StyledContainer>
      <div>
        {(can(Actions.READ, Subjects.ACCOUNTS) &&
          cannot(Actions.READ, Subjects.OWN_ACCOUNT)) ||
        can(Actions.READ, Subjects.ASSIGNED_ACCOUNTS) ? (
          <SidebarAccountSelect />
        ) : (
          <SidebarAccountName />
        )}
        {!!account?.freeTrialEndsAt && <FreeTrialBox />}

        <StyledPanelMenu
          model={getMenu(
            canReach,
            cannot,
            accountCanUseFeature,
            user?.accountsCount as number,
            onClick,
            current,
            t,
            account,
            isBranchAdmin,
            incompleteTaskCount,
            showPhishingIntro,
            isAwarenessHidden,
          )}
          className="mt-3"
        />
      </div>
      {account && (
        <StyledFooter>
          <div className="mb-3">
            {t('sidebar.accountId')}
            <br />
            <span>{account.id}</span>
          </div>

          {can(Actions.READ, Subjects.HUBSPOT_ACCOUNT_LINK) &&
            account?.hubspot?.id &&
            account?.hubspot?.url && (
              <div className="mb-3">
                {t('hubspot.companyId')}
                <br />
                <a
                  href={account.hubspot.url}
                  target="_blank"
                  className="mt-3"
                  rel="noopener noreferrer"
                >
                  <span>{account.hubspot.id}</span>
                  <i className="pi pi-external-link x2 ml-2" />
                </a>
              </div>
            )}
        </StyledFooter>
      )}
    </StyledContainer>
  );
};
